import {action, observable, makeObservable, runInAction} from "mobx";
import {AppConfiguration} from "./config/app-configuration";
import EsriMap from "@arcgis/core/Map";
import {WebMapConfiguration} from "./config/web-map-configuration";
import Portal from "@arcgis/core/portal/Portal";
import Basemap from "@arcgis/core/Basemap";
import {PortalBasemapsSourceConfiguration} from "./config/basemap-gallery-configuration";
import {FormatUtils} from "../utils/format-utils";

export interface BasemapProps {
    id: string;
    title?: string;
    tags?: string[];
}

export class MapState {
    @observable status: string;
    @observable map: EsriMap | undefined;
    @observable view: __esri.MapView | __esri.SceneView | undefined;
    @observable layers = observable.map<string, __esri.Layer>();
    @observable groupLayers = observable.map<string, __esri.Layer>();
    @observable visibleFilterlayers = observable.map<string, __esri.Layer>();
    @observable layerViews = observable.map<string, __esri.FeatureLayerView>();
    @observable highlightedFeature: __esri.Handle | null;
    @observable activeLayers: string[];
    @observable selectedFeature: __esri.Graphic | null;
    @observable basemap: BasemapProps | null;
    @observable basemapOpacity: number;
    @observable defaultBasemapTitle: string;
    @observable basemapAttribution: string;
    @observable webMap: WebMapConfiguration;

    @observable featureInfo: string;
    appConfig: AppConfiguration;
    defaultBasemap: BasemapProps;

    constructor() {
        makeObservable(this);
        this.setStatus("loading");
        this.setBasemapOpacity(100);
    }

    @action setView(view?: __esri.MapView | __esri.SceneView): void {
        this.view = view;
    }

    @action setMap(map?: EsriMap): void {
        this.map = map;
    }

    @action setStatus(status: string): void {
        this.status = status;
    }

    @action setFeatureInfo(info: string): void {
        this.featureInfo = info;
    }

    @action clearFeatureInfo(): void {
        this.featureInfo = "";
    }

    @action addLayer(key: string, layer: __esri.Layer): void {
        this.layers.set(key, layer);
    }
    @action removeLayer(key: string): void {
        this.layers.delete(key);
    }
    @action addGroupLayer(key: string, layer: __esri.Layer): void {
        this.groupLayers.set(key, layer);
    }
    @action removeGroupLayer(key: string): void {
        this.groupLayers.delete(key);
    }
    @action addVisibleFilterLayer(key: string, layer: __esri.Layer): void {
        this.visibleFilterlayers.set(key, layer);
    }
    @action removeVisibleFilterLayer(key: string): void {
        this.visibleFilterlayers.delete(key);
    }

    @action addLayerView(key: string, layerView: __esri.FeatureLayerView): void {
        this.layerViews.set(key, layerView);
    }
    @action removeLayerView(key: string): void {
        this.layerViews.delete(key);
    }

    @action selectFeature(feature: __esri.Graphic | null): void {
        this.selectedFeature = feature;
    }
    @action setActiveLayers(layers: string[]): void {
        this.activeLayers = layers;
    }
    /**
     * Highlighted feature in the map
     * At the moment, only one feature can be highlighted at the same time
     *
     * @param feature
     */
    @action highlightFeature(feature: __esri.Handle): void {
        this.highlightedFeature = feature;
    }

    /**
     * Remove highlight from feature
     */
    @action unhighlightFeature(): void {
        if (!this.highlightedFeature) {
            return;
        }
        this.highlightedFeature.remove();
        this.highlightedFeature = null;
    }

    /**
     * Set Basemap
     *
     * @param {string} [urlOrTag]: A Tag or the URL to the REST endpoint of the map service.
     * @param {string} [title]: Only allowed if layers is a string.
     */
    @action setBasemap(urlOrTag: string, title?: string): void {
        if (urlOrTag.indexOf("http") === -1) {
            const portal = new Portal();
            portal.load().then(()=> {
                runInAction(()=>{
                    const {basemapGallery} = this.appConfig;
                    // TODO: Query sollte auch als Objekt funktionieren
                    portal.fetchBasemaps(basemapGallery?.source
                        ? (basemapGallery?.source as PortalBasemapsSourceConfiguration).query
                        : undefined).then((basemaps)=>{
                        let basemapItem;
                        // Grundkarte anhand Tag suchen
                        if(urlOrTag!==""){
                            basemapItem = basemaps.find((b) => b.portalItem.tags.includes(urlOrTag));
                        // Grundkarte nach Titel suchen
                        } else if (title){
                            basemapItem = basemaps.find((b) => FormatUtils.formatTitle(b.portalItem.title)
                                === FormatUtils.formatTitle(title)
                                && b.portalItem.tags.filter(
                                    tag => basemapGallery.excludeTags?.includes(tag)).length === 0);
                        }
                        if (basemapItem && this.basemap?.id !== basemapItem.portalItem.id) {
                            const itemId = basemapItem.portalItem.id;
                            const basemap: BasemapProps = {
                                id: itemId,
                                title: FormatUtils.formatTitle(basemapItem.portalItem.title),
                                tags: basemapItem.portalItem.tags
                            };
                            this.setBasemapById(basemap);
                            runInAction(()=>{
                                if(this.map && this.basemap) {
                                    this.map.basemap = new Basemap({
                                        portalItem: {
                                            id: this.basemap.id
                                        }
                                    });
                                    this.map.basemap.load().then(() => {
                                        this.map?.basemap.baseLayers.forEach((layer: __esri.Layer) => {
                                            layer.opacity = this.basemapOpacity / 100;
                                        });
                                    });
                                }
                            });
                        }
                    });
                });
            });
        } else {
            this.basemap = {
                id: urlOrTag,
                title: title? title: undefined
            };
        }
    }
    /**
     * Set Basemap
     *
     * @param {string|BasemapProps} [basemapId]: Portal Id of basemap item.
     * @param {string} [title]
     * @param {array} [tags]
     */
    @action setBasemapById(basemapId: string | BasemapProps, title?: string, tags?: string[]): void {
        if (typeof basemapId !== "string") {
            this.basemap = basemapId as BasemapProps;
        } else {
            this.basemap = {
                id: basemapId,
                title: title? title: undefined,
                tags: tags? tags: undefined,
            };
        }
    }
    @action setBasemapOpacity(basemapOpacity: number): void {
        this.basemapOpacity = basemapOpacity;
    }
    @action setBasemapAttribution(attribution: string): void {
        this.basemapAttribution = attribution;
    }

    @action setDefaultBasemapTitle(title: string): void {
        this.defaultBasemapTitle = FormatUtils.formatTitle(title);
    }
    @action setDefaultBasemap(basemap: BasemapProps): void {
        this.defaultBasemap = basemap;
        if(basemap.title){
            if(this.defaultBasemapTitle !== FormatUtils.formatTitle(basemap.title)){
                this.defaultBasemapTitle = FormatUtils.formatTitle(basemap.title);
            }
            this.defaultBasemap.title = this.defaultBasemapTitle;
        }
    }

    @action setWebMap(webMap: WebMapConfiguration): void {
        if (typeof this.webMap !== "undefined" && this.webMap.portalItem.id === webMap.portalItem.id) {
            return;
        }
        this.webMap = webMap;
    }
    setAppConfig(appConfig: AppConfiguration): void {
        this.appConfig = appConfig;
    }
}
